import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CREDIT_SECTIONS, isSafeExternalUrl, type CreditEntry } from '../state/credits.js';

/** Credits screen (spec §18, Phase 4b): art, data and software the game is built on. */
export function CreditsPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Focus moves into the dialog and back to whatever opened it when it closes.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      opener?.focus();
    };
  }, [onClose]);

  return (
    <div className="overlay credits-overlay" onClick={onClose}>
      <div
        className="panel credits-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="credits-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h1 id="credits-title">{t('credits.title')}</h1>
        <p className="muted">{t('credits.intro')}</p>
        {CREDIT_SECTIONS.map((section) => (
          <section key={section.id}>
            <h2>{t(section.titleKey)}</h2>
            <ul className="credits-list">
              {section.entries.map((entry) => (
                <CreditItem key={entry.id} entry={entry} />
              ))}
            </ul>
          </section>
        ))}
        <button ref={closeRef} type="button" className="secondary" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}

function CreditItem({ entry }: { entry: CreditEntry }) {
  const { t } = useTranslation();
  return (
    <li>
      <ExternalLink href={entry.url}>{entry.name}</ExternalLink>{' '}
      <span className="muted">{t('credits.by', { author: entry.author })}</span>
      <br />
      <span>{t(entry.usedForKey)}</span>
      <br />
      <span className="muted">
        {entry.licenceUrl ? (
          <ExternalLink href={entry.licenceUrl}>
            {t('credits.licence', { licence: entry.licence })}
          </ExternalLink>
        ) : (
          t('credits.licence', { licence: entry.licence })
        )}
      </span>
    </li>
  );
}

function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  const { t } = useTranslation();
  if (!isSafeExternalUrl(href)) return <>{children}</>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
      <span className="sr-only"> {t('credits.opensInNewTab')}</span>
    </a>
  );
}

/** The short art credit plus a button that opens the full credits screen. */
export function CreditsLink() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <p className="credits">
        {t('credits.tiles')} ·{' '}
        <button type="button" className="link" onClick={() => setOpen(true)}>
          {t('credits.open')}
        </button>
      </p>
      {open && <CreditsPanel onClose={close} />}
    </>
  );
}
